import * as React from 'react';
import { Radio, createStyles, Group } from '@mantine/core';
import { useTranslation } from 'react-i18next';

// Styles
import { getCommonFieldStyles } from '../helpers/fieldStyles';

// Hooks
import { useFieldTranslation } from '../hooks/useFieldTranslation';
import { useEnumDataSet } from '../hooks/useEnumDataSet';

// Types
import { GenericFieldProps } from '../types';

const useStyles = createStyles((theme) => {
    const commonFieldStyles = getCommonFieldStyles(theme);

    return {
        root: commonFieldStyles.root,
        input: commonFieldStyles.input,
        labelHighlight: commonFieldStyles.labelHighlight,
        leftGroupPosition: commonFieldStyles.leftGroupPosition,
        rightGroupPosition: commonFieldStyles.rightGroupPosition,
        middleGroupPosition: commonFieldStyles.middleGroupPosition,
        readonlyInput: commonFieldStyles.readonlyInput,
        required: commonFieldStyles.required,
        hiddenDisplay: commonFieldStyles.hiddenDisplay,
        label: commonFieldStyles.label,
        icon: commonFieldStyles.icon,
        error: commonFieldStyles.error,
        wrapper: commonFieldStyles.wrapper,
        readonlyWrapper: commonFieldStyles.readonlyWrapper,
        description: commonFieldStyles.description,
        radioGroup: {
            height: '53px',
            marginTop: 'calc(0.625rem / 2)',
            marginBottom: 'calc(0.625rem / 2)',
            paddingBottom: '0.5rem'
        }
    };
});

export interface YesNoRadioProps extends GenericFieldProps {
    required?: boolean;
    value?: boolean;
    onChange: (value: string | boolean) => void;
    readOnly?: boolean;
    error?: string | null;
}

export const YesNoRadio: React.FC<YesNoRadioProps> = ({
    tooltipKey,
    descriptionKey,
    labelKey,
    prefix,
    parentform,
    onChange,
    readOnly,
    value,
    disabled,
    targetperiod,
    highlight,
    error,
    ...props
}) => {
    const { classes, cx } = useStyles();
    const inputLabel = useFieldTranslation({ key: labelKey, formData: parentform?.values, targetPeriod: targetperiod });
    const descriptionLabel = useFieldTranslation({
        key: descriptionKey,
        formData: parentform?.values,
        targetPeriod: targetperiod
    });
    const tooltipLabel = useFieldTranslation({
        key: tooltipKey,
        formData: parentform?.values,
        targetPeriod: targetperiod
    });
    const { getTranslatedDataSet } = useEnumDataSet();
    const options = React.useMemo(() => getTranslatedDataSet('YES_OR_NO_BINARY'), [getTranslatedDataSet]);
    const adaptedValue = React.useMemo(() => {
        if (typeof value !== 'undefined') {
            return value === true ? 'y' : 'n';
        } else {
            return undefined;
        }
    }, [value]);
    const onChangeWrapper = (newValue: string | boolean) => {
        onChange(newValue === 'y');
    };

    return (
        <Radio.Group
            name={prefix}
            label={inputLabel}
            description={descriptionLabel}
            onChange={onChangeWrapper}
            value={adaptedValue}
            error={error}
            withAsterisk={props?.required}
            classNames={{
                label: cx(classes.label, {
                    [classes.labelHighlight]: typeof highlight === 'string' && highlight === 'true'
                })
            }}
            {...props}
        >
            <Group className={classes.radioGroup}>
                {options.map(({ value, label }) => (
                    <Radio value={value} label={label} key={value} disabled={disabled} />
                ))}
            </Group>
        </Radio.Group>
    );
};
